import { useRef, useState } from "react";
import axios from "axios";
import { Download, Loader2, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRANSFER_CONFIG = {
  inbox: {
    extension: "json",
    accept: ".json,application/json",
    exportEndpoint: "/api/data-transfer/inbox/export-json",
    importEndpoint: "/api/data-transfer/inbox/import-json",
    fallbackFilename: "gimmicks_conversations.json",
  },
  interesados: {
    extension: "xlsx",
    accept: ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    exportEndpoint: "/api/data-transfer/interesados/export-excel",
    importEndpoint: "/api/data-transfer/interesados/import-excel",
    fallbackFilename: "interesados_gimmicks.xlsx",
  },
  leads: {
    extension: "xlsx",
    accept: ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    exportEndpoint: "/api/data-transfer/leads/export-excel",
    importEndpoint: "/api/data-transfer/leads/import-excel",
    fallbackFilename: "leads_gimmicks.xlsx",
  },
};

const LABELS = {
  inbox: "Inbox",
  interesados: "Interesados",
  leads: "Leads",
};

const getDownloadFilename = (contentDisposition, fallback) => {
  const match = String(contentDisposition || "").match(/filename\*?=(?:UTF-8''|\")?([^\";]+)/i);
  return match?.[1] ? decodeURIComponent(match[1].replace(/\"/g, "").trim()) : fallback;
};

export default function DataTransferButtons({ dataset, onImported, className = "" }) {
  const { getAuthHeaders, user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [learningImporting, setLearningImporting] = useState(false);
  const inputRef = useRef(null);
  const learningInputRef = useRef(null);

  if (user?.role !== "admin") return null;

  const label = LABELS[dataset] || dataset;
  const config = TRANSFER_CONFIG[dataset];
  if (!config) return null;

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}${config.exportEndpoint}`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = getDownloadFilename(
        response.headers?.["content-disposition"],
        config.fallbackFilename
      );
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`${label} exportado correctamente en ${config.extension.toUpperCase()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Error al exportar ${label}`);
    } finally {
      setExporting(false);
    }
  };

  const handleLearningImport = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selectedFiles.length) return;

    const invalid = selectedFiles.find((file) => !file.name.toLowerCase().endsWith(".json"));
    if (invalid) {
      toast.error(`Solo se admiten archivos JSON de casos exitosos. Archivo inválido: ${invalid.name}`);
      return;
    }

    const targetHost = (() => {
      try {
        return new URL(API_URL || window.location.origin, window.location.origin).host || window.location.host;
      } catch {
        return window.location.host;
      }
    })();
    if (/preview\.emergentagent\.com/i.test(targetHost)) {
      toast.error("Importación de memoria bloqueada en Preview. Despliega el cambio y carga los casos desde el Inbox de producción.");
      return;
    }

    const confirmed = window.confirm(
      `¿Importar ${selectedFiles.length} archivo(s) de casos exitosos?\n\nLos casos se guardarán en la base de datos conectada a ESTE Inbox de producción. Solo se aceptan memorias aprobadas y no se crean hechos oficiales de Gimmicks.`
    );
    if (!confirmed) return;

    setLearningImporting(true);
    try {
      const body = new FormData();
      selectedFiles.forEach((file) => body.append("files", file));
      const response = await axios.post(
        `${API_URL}/api/data-transfer/inbox/import-learning-cases`,
        body,
        { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
      );
      const { inserted = 0, updated = 0, skipped = 0, validated_cases = 0 } = response.data || {};
      toast.success(
        `Memoria: ${validated_cases} casos validados · ${inserted} nuevos, ${updated} actualizados${skipped ? `, ${skipped} ya existentes` : ""}`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar casos exitosos");
    } finally {
      setLearningImporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const expectedSuffix = `.${config.extension}`;
    if (!file.name.toLowerCase().endsWith(expectedSuffix)) {
      toast.error(`${label} requiere un archivo ${expectedSuffix}`);
      return;
    }

    const confirmed = window.confirm(
      `¿Importar ${label} desde ${file.name}?\n\nLa importación fusiona y actualiza registros existentes. No elimina datos.`
    );
    if (!confirmed) return;

    setImporting(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await axios.post(
        `${API_URL}${config.importEndpoint}`,
        body,
        {
          headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
        }
      );
      const { inserted = 0, updated = 0, skipped = 0 } = response.data || {};
      toast.success(
        `${label}: ${inserted} nuevos, ${updated} actualizados${skipped ? `, ${skipped} omitidos` : ""}`
      );
      await onImported?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Error al importar ${label}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid={`data-transfer-${dataset}`}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleExport}
        disabled={exporting || importing || learningImporting}
        data-testid={`export-${dataset}-btn`}
      >
        {exporting ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Download size={16} className="mr-1" />}
        Exportar
      </Button>

      <input
        ref={inputRef}
        type="file"
        accept={config.accept}
        onChange={handleImport}
        className="hidden"
        data-testid={`import-${dataset}-input`}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={exporting || importing || learningImporting}
        data-testid={`import-${dataset}-btn`}
      >
        {importing ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Upload size={16} className="mr-1" />}
        Importar
      </Button>

      {dataset === "inbox" && (
        <>
          <input
            ref={learningInputRef}
            type="file"
            accept=".json,application/json"
            multiple
            onChange={handleLearningImport}
            className="hidden"
            data-testid="import-learning-cases-input"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            onClick={() => learningInputRef.current?.click()}
            disabled={exporting || importing || learningImporting}
            title="Importar memoria de casos exitosos"
            aria-label="Importar memoria de casos exitosos"
            data-testid="import-learning-cases-btn"
          >
            {learningImporting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
          </Button>
        </>
      )}
    </div>
  );
}
